import { Duration, LocalDate, LocalDateTime, LocalTime } from '@js-joda/core';
import { getLocalDateTime } from './timeDisplay';
import React from 'react';
import pluralize from 'pluralize';

import { TimeZoneContext } from '@tock/coretypes';

type TemporalUnitKey = 'WEEKS' | 'DAYS' | 'HOURS' | 'MINUTES' | 'SECONDS';
const OrderedTemporalUnitKeys: TemporalUnitKey[] = ['WEEKS', 'DAYS', 'HOURS', 'MINUTES', 'SECONDS'];

function getSmallestUnit(unitsToConsider: TemporalUnitKey[]): TemporalUnitKey {
  return (
    OrderedTemporalUnitKeys.reverse().filter((key) => unitsToConsider.includes(key))?.[0] ??
    'SECONDS'
  );
}

interface Props {
  startTime: string | LocalDateTime | LocalTime;
  dayOfStartTimeIfLocalTime?: LocalDate;
  startTimeZone?: string;

  endTime: string | LocalDateTime | LocalTime;
  dayOfEndTimeIfLocalTime?: LocalDate;
  endTimeZone?: string;

  unitsToDisplay?: TemporalUnitKey[];

  positiveDurationPrefix?: string;
  positiveDurationSuffix?: string;
  negativeDurationPrefix?: string;
  negativeDurationSuffix?: string;
  zeroDurationText?: string;
  display?: 'short' | 'long';
}

interface DurationDisplayItem {
  suffix: string;
  amount: number;
  key: TemporalUnitKey;
}

const DisplayUnitShortSuffixes: { [K in TemporalUnitKey]: string } = {
  WEEKS: 'w',
  DAYS: 'd',
  HOURS: 'h',
  MINUTES: 'm',
  SECONDS: 's',
};

const DisplayUnitLongSuffixes: { [K in TemporalUnitKey]: string } = {
  WEEKS: 'week',
  DAYS: 'day',
  HOURS: 'hour',
  MINUTES: 'minute',
  SECONDS: 'second',
};

function getSuffix(amount: number, key: TemporalUnitKey, display: 'short' | 'long') {
  return display === 'short'
    ? DisplayUnitShortSuffixes[key]
    : ` ${pluralize(DisplayUnitLongSuffixes[key], amount)}`;
}

export const DisplayDuration = ({
  startTime,
  dayOfStartTimeIfLocalTime = LocalDate.EPOCH_0,
  startTimeZone,
  endTime,
  endTimeZone,
  unitsToDisplay = ['DAYS', 'HOURS', 'MINUTES'],
  positiveDurationPrefix,
  positiveDurationSuffix,
  negativeDurationPrefix,
  negativeDurationSuffix,
  zeroDurationText,
  display = 'short',
}: React.PropsWithChildren<Props>) => {
  const timeZone = React.useContext(TimeZoneContext);

  const normalizedStartTime = getLocalDateTime(
    startTime instanceof LocalTime ? startTime.atDate(dayOfStartTimeIfLocalTime) : startTime,
    startTimeZone || timeZone
  );
  const normalizedEndTime = getLocalDateTime(
    endTime instanceof LocalTime ? endTime.atDate(dayOfStartTimeIfLocalTime) : endTime,
    endTimeZone || timeZone
  );

  if (!normalizedStartTime || !normalizedEndTime) {
    return null;
  }

  let duration = Duration.between(normalizedStartTime, normalizedEndTime);
  const durationIsPositive = !duration.isNegative();

  const displayItems: DurationDisplayItem[] = [];
  const addItemToDisplay = (amount: number, key: TemporalUnitKey) => {
    if (amount === 0) {
      return;
    }
    displayItems.push({
      amount,
      key,
      suffix: getSuffix(amount, key, display),
    });
  };

  if (unitsToDisplay.includes('WEEKS')) {
    const daysForWeeksCalculation = duration.toDays();
    const weeks = (daysForWeeksCalculation - (daysForWeeksCalculation % 7)) / 7;
    addItemToDisplay(Math.abs(weeks), 'WEEKS');
    duration = duration.minusDays(daysForWeeksCalculation);
  }
  if (unitsToDisplay.includes('DAYS')) {
    const days = duration.toDays();
    addItemToDisplay(Math.abs(days), 'DAYS');
    duration = duration.minusDays(days);
  }
  if (unitsToDisplay.includes('HOURS')) {
    const hours = duration.toHours();
    addItemToDisplay(Math.abs(hours), 'HOURS');
    duration = duration.minusHours(hours);
  }
  if (unitsToDisplay.includes('MINUTES')) {
    const minutes = duration.toMinutes();
    addItemToDisplay(Math.abs(minutes), 'MINUTES');
    duration = duration.minusMinutes(minutes);
  }
  if (unitsToDisplay.includes('SECONDS')) {
    const seconds = duration.seconds();
    addItemToDisplay(Math.abs(seconds), 'SECONDS');
    duration = duration.minusSeconds(seconds);
  }

  // Duration is 0 of the smallest units to be considered
  if (displayItems.length === 0) {
    const smallestUnit = getSmallestUnit(unitsToDisplay);
    return <>{zeroDurationText ? zeroDurationText : `0${getSuffix(0, smallestUnit, display)}`}</>;
  }

  return (
    <>
      {durationIsPositive && positiveDurationPrefix ? `${positiveDurationPrefix} ` : ''}
      {!durationIsPositive && negativeDurationPrefix ? `${negativeDurationPrefix} ` : ''}
      {displayItems.map(({ amount, key, suffix }, index) => (
        <React.Fragment key={key}>
          {amount}
          {display === 'short' ? suffix : pluralize(suffix, amount)}
          {index !== displayItems.length - 1 && ' '}
        </React.Fragment>
      ))}
      {durationIsPositive && positiveDurationSuffix ? ` ${positiveDurationSuffix}` : ''}
      {!durationIsPositive && negativeDurationSuffix ? ` ${negativeDurationSuffix}` : ''}
    </>
  );
};
